import api from './api';

export interface StudyModule {
  _id: string;
  title: string;
  description?: string;
  status: 'not_started' | 'in_progress' | 'completed';
  order: number;
  content?: string;
  lastViewedAt?: string;
}

export interface Study {
  _id: string;
  userId: string;
  title?: string;
  description?: string;
  subject?: string;
  start?: string;
  end?: string;
  category?: string;
  theme?: string;
  what?: string;
  where?: string;
  link?: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'paused';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  tags?: string[];
  estimatedDuration?: number;
  actualDuration?: number;
  notes?: string;
  startedAt?: string;
  completedAt?: string;
  dueDate?: string;
  progressPercentage: number;
  metadata?: Record<string, any>;
  reminderEnabled?: boolean;
  reminderFrequency?: number;
  reminderUnit?: 'days' | 'weeks' | 'months';
  lastReminderSent?: string;
  nextReminderDate?: string;
  modules?: StudyModule[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateStudyDto {
  title?: string;
  description?: string;
  subject?: string;
  start?: string;
  end?: string;
  category?: string;
  theme?: string;
  what?: string;
  where?: string;
  link?: string;
  status?: Study['status'];
  priority?: Study['priority'];
  tags?: string[];
  estimatedDuration?: number;
  notes?: string;
  dueDate?: string;
  progressPercentage?: number;
  metadata?: Record<string, any>;
  reminderEnabled?: boolean;
  reminderFrequency?: number;
  reminderUnit?: 'days' | 'weeks' | 'months';
}

export interface UpdateStudyDto extends Partial<CreateStudyDto> {
  actualDuration?: number;
  startedAt?: string;
  completedAt?: string;
}

export interface FilterStudyDto {
  status?: Study['status'];
  what?: string;
  priority?: Study['priority'];
  subject?: string;
  category?: string;
  theme?: string;
  where?: string;
  tags?: string[];
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface StudyListResponse {
  studies: Study[];
  total: number;
  page: number;
  limit: number;
}

export interface StudyStatistics {
  total: number;
  byStatus: {
    notStarted: number;
    inProgress: number;
    completed: number;
    paused: number;
  };
  completionRate: number;
  byResourceType: Record<string, number>;
  byPriority: Record<string, number>;
  upcomingDue: Study[];
}

export interface SubjectStatistics {
  subject: string;
  total: number;
  notStarted: number;
  inProgress: number;
  completed: number;
  paused: number;
  completionRate: number;
  modulesTotal: number;
  modulesCompleted: number;
  modulesWithAiContent: number;
}

export interface ModulesStatistics {
  totalModules: number;
  completedModules: number;
  inProgressModules: number;
  notStartedModules: number;
  aiGeneratedModules: number;
  aiModulesLimit: number;
  aiModulesUnlimited: boolean;
  aiModulesRemaining: number;
}

const studiesService = {
  // Create a new study
  createStudy: async (data: CreateStudyDto): Promise<Study> => {
    const response = await api.post('/studies-control', data);
    return response.data;
  },

  // Get all studies with filters
  getStudies: async (filters?: FilterStudyDto): Promise<StudyListResponse> => {
    const response = await api.get('/studies-control', { params: filters });
    return response.data;
  },

  // Get a single study by ID
  getStudy: async (id: string): Promise<Study> => {
    const response = await api.get(`/studies-control/${id}`);
    return response.data;
  },

  // Update a study
  updateStudy: async (id: string, data: UpdateStudyDto): Promise<Study> => {
    const response = await api.patch(`/studies-control/${id}`, data);
    return response.data;
  },

  // Delete a study
  deleteStudy: async (id: string): Promise<void> => {
    await api.delete(`/studies-control/${id}`);
  },

  // Copy a study
  copyStudy: async (id: string): Promise<Study> => {
    const response = await api.post(`/studies-control/${id}/copy`);
    return response.data;
  },

  // Generate study modules (topics) with AI based on subject/description
  generateModules: async (id: string): Promise<Study> => {
    const response = await api.post(`/studies-control/${id}/generate-modules`);
    return response.data;
  },

  // Create a module manually
  createModule: async (id: string, data: { title: string; topics: string[] }): Promise<Study> => {
    const response = await api.post(`/studies-control/${id}/modules`, data);
    return response.data;
  },

  // Update a module
  updateModule: async (
    id: string,
    moduleId: string,
    data: { title?: string; description?: string; status?: StudyModule['status'] },
  ): Promise<Study> => {
    const response = await api.patch(`/studies-control/${id}/modules/${moduleId}`, data);
    return response.data;
  },

  // Update a module's status
  updateModuleStatus: async (id: string, moduleId: string, status: StudyModule['status']): Promise<Study> => {
    return studiesService.updateModule(id, moduleId, { status });
  },

  // Delete a module
  deleteModule: async (id: string, moduleId: string): Promise<Study> => {
    const response = await api.delete(`/studies-control/${id}/modules/${moduleId}`);
    return response.data;
  },

  // Record that the user viewed a module (updates its lastViewedAt)
  markModuleViewed: async (id: string, moduleId: string): Promise<Study> => {
    const response = await api.post(`/studies-control/${id}/modules/${moduleId}/view`);
    return response.data;
  },

  // Generate the study text/content for a module with AI
  generateModuleContent: async (
    id: string,
    moduleId: string,
    options?: { append?: boolean; pageNumber?: number },
  ): Promise<Study> => {
    const response = await api.post(
      `/studies-control/${id}/modules/${moduleId}/generate-content`,
      options || {},
    );
    return response.data;
  },

  // Get statistics
  getStatistics: async (): Promise<StudyStatistics> => {
    const response = await api.get('/studies-control/statistics');
    return response.data;
  },

  // Get statistics by subject
  getStatisticsBySubject: async (): Promise<SubjectStatistics[]> => {
    const response = await api.get('/studies-control/statistics/by-subject');
    return response.data;
  },

  // Get AI-generated modules progress and free-tier usage
  getModulesStatistics: async (): Promise<ModulesStatistics> => {
    const response = await api.get('/studies-control/statistics/modules');
    return response.data;
  },

  // Get all subjects
  getSubjects: async (): Promise<string[]> => {
    const response = await api.get('/studies-control/subjects');
    return response.data;
  },

  // Get all categories
  getCategories: async (subject?: string): Promise<string[]> => {
    const params = subject ? { subject } : {};
    const response = await api.get('/studies-control/categories', { params });
    return response.data;
  },

  // Get all tags
  getTags: async (): Promise<string[]> => {
    const response = await api.get('/studies-control/tags');
    return response.data;
  },
};

export default studiesService;
